import { StartEndTimeMode } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { LIVE_MODE_UPDATE_INTERVAL_MS } from "./constants";
import { GlobalSearchDataProvider } from "./GlobalSearchDataProvider";

export interface MapNavigationParams {
  resourceId: string;
  officeId?: string;
  floorId?: string;
  date?: Date;
  endDate?: Date;
  center?: boolean;
  callback?: () => void;
}

export type MapNavigatorFunction = (params: MapNavigationParams) => void;

export type OpenResourceInfoFunction = (resourceId: string) => void;

export interface MapControllers {
  changeOffice: (officeId: string) => void;
  changeStartDate: (date: Date) => void;
  changeEndTime: (date: Date | null) => void;
  setTimeMode: (mode: StartEndTimeMode) => void;
  navigateToOffice: (
    officeId: string,
    date?: Date,
    timeMode?: StartEndTimeMode,
    endDate?: Date
  ) => void;
}

export type GlobalSearchOpenParamsOptions = {
  officeId?: string | null | undefined;
  date?: Date | null;
  endDate?: Date | null;
  timeMode?: StartEndTimeMode;
};
interface GlobalSearchContextType {
  isOpen: boolean;
  open: (query?: string, options?: GlobalSearchOpenParamsOptions) => void;
  close: () => void;
  toggle: () => void;
  registerMapNavigator: (navigator: MapNavigatorFunction | null) => void;
  mapNavigator: MapNavigatorFunction | null;
  registerOpenResourceInfo: (
    openResourceInfo: OpenResourceInfoFunction | null
  ) => void;
  openResourceInfo: OpenResourceInfoFunction | null;
  registerMapControllers: (controllers: MapControllers | null) => void;
  mapControllers: MapControllers | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  timeMode: StartEndTimeMode;
  setTimeMode: (mode: StartEndTimeMode) => void;
  selectedOfficeId: string | null | undefined;
  setSelectedOfficeId: (officeId: string | null | undefined) => void;
  selectedOfficeIds: string[];
  setSelectedOfficeIds: (officeIds: string[]) => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextType | undefined>(
  undefined
);

export function useGlobalSearch() {
  const context = useContext(GlobalSearchContext);
  if (!context) {
    throw new Error("useGlobalSearch must be used within GlobalSearchProvider");
  }
  return context;
}

interface GlobalSearchProviderProps {
  children: React.ReactNode;
}

export function GlobalSearchProvider({ children }: GlobalSearchProviderProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const [isOpen, setIsOpen] = useState(false);
  const [mapNavigator, setMapNavigator] = useState<MapNavigatorFunction | null>(
    null
  );
  const [openResourceInfo, setOpenResourceInfo] =
    useState<OpenResourceInfoFunction | null>(null);
  const [mapControllers, setMapControllers] = useState<MapControllers | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [timeMode, setTimeMode] = useState<StartEndTimeMode>("manual");
  const [selectedOfficeId, setSelectedOfficeId] = useState<
    string | null | undefined
  >(null);
  const [selectedOfficeIds, setSelectedOfficeIds] = useState<string[]>([]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [authenticatedUser?.organization?.slug]);

  const registerMapNavigator = useCallback(
    (navigator: MapNavigatorFunction | null) => {
      setMapNavigator(() => navigator);
    },
    []
  );

  const registerOpenResourceInfo = useCallback(
    (openResourceInfo: OpenResourceInfoFunction | null) => {
      setOpenResourceInfo(() => openResourceInfo);
    },
    []
  );

  const registerMapControllers = useCallback(
    (controllers: MapControllers | null) => {
      setMapControllers(() => controllers);
    },
    []
  );

  // Handle live mode - update dates periodically
  useEffect(() => {
    if (timeMode !== "live") return;

    const updateLiveDates = () => {
      const now = new Date();
      setStartDate(now);
      const end = new Date(now);
      end.setHours(end.getHours() + 1);
      setEndDate(end);
    };

    updateLiveDates();
    const interval = setInterval(updateLiveDates, LIVE_MODE_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [timeMode]);

  const value: GlobalSearchContextType = {
    isOpen,
    open: useCallback(
      (query?: string, options?: GlobalSearchOpenParamsOptions) => {
        // we pass empty string to open with empty searchbar
        if (query !== undefined && query !== searchQuery) {
          setSearchQuery(query);
        }

        // Set office, date, and time if provided
        if (
          options?.officeId !== undefined &&
          options.officeId !== selectedOfficeId
        ) {
          setSelectedOfficeId(options.officeId);
        }
        if (!!options?.date && options.date !== startDate) {
          setStartDate(options.date);
        }
        if (!!options?.endDate && options.endDate !== endDate) {
          setEndDate(options.endDate);
        }
        if (!!options?.timeMode && options.timeMode !== timeMode) {
          setTimeMode(options.timeMode);
        }

        setIsOpen(true);
      },
      [searchQuery, setSearchQuery]
    ),
    close: useCallback(() => setIsOpen(false), []),
    toggle: useCallback(() => setIsOpen((prev) => !prev), []),
    registerMapNavigator,
    mapNavigator,
    registerOpenResourceInfo,
    openResourceInfo,
    registerMapControllers,
    mapControllers,
    searchQuery,
    setSearchQuery,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    timeMode,
    setTimeMode,
    selectedOfficeId,
    setSelectedOfficeId,
    selectedOfficeIds,
    setSelectedOfficeIds
  };

  return (
    <GlobalSearchContext.Provider value={value}>
      <GlobalSearchDataProvider
        searchQuery={searchQuery}
        isOpen={isOpen}
        startDate={startDate}
        endDate={endDate}
        selectedOfficeId={selectedOfficeId}
        selectedOfficeIds={selectedOfficeIds}
      >
        {children}
      </GlobalSearchDataProvider>
    </GlobalSearchContext.Provider>
  );
}
